import { useContext, useSyncExternalStore } from 'react';

import { CheckoutContext } from '@/context/CheckoutContext';
import {
  getApplePayTabFallbackReason,
  hasApplePayDomainFailure,
  shouldUseApplePayTabFallback,
  subscribeToApplePayDomainFailure,
  type ApplePayTabFallbackReason,
} from '@/utils/applePayTabFallback';

/**
 * Re-render this component when the domain-failure latch flips. The
 * iOS <= 16 half of the gate is static, but a domain failure is only
 * learned when the player clicks the REAL button and Apple rejects the
 * merchant session — the Apple Pay slot has to swap in the fake button
 * right then, without a reload.
 */
const useDomainFailureLatch = (): boolean =>
  useSyncExternalStore(subscribeToApplePayDomainFailure, hasApplePayDomainFailure);

/** Reactive `shouldUseApplePayTabFallback`, with flags read from context. */
export const useApplePayTabFallbackGate = (): boolean => {
  const { featureFlags } = useContext(CheckoutContext);
  useDomainFailureLatch();
  return shouldUseApplePayTabFallback(featureFlags);
};

/** Reactive `getApplePayTabFallbackReason` — for analytics on the fallback itself. */
export const useApplePayTabFallbackReason = (): ApplePayTabFallbackReason | null => {
  const { featureFlags } = useContext(CheckoutContext);
  useDomainFailureLatch();
  return getApplePayTabFallbackReason(featureFlags);
};
